// page-backlog-review-support.ts — R6 : composition du support de comité.
//
// Deux colonnes : à gauche ce qu'on présente, à droite le plan qui s'écrit tout
// seul. L'écran ne décide rien — il ORDONNE et il RETIRE. La position d'une
// recommandation, elle, se décide dans la revue.
//
// Trois règles de cet écran, et elles portent tout le reste :
//
// 1. **Les DEUX sections se remplissent toutes seules** (2026-08-26). On y
//    ordonne et on y DÉCOCHE, jamais on n'ajoute : le drapeau se pose dans la
//    revue, et « Pour information » suit les piles. Ajouter à la main ferait
//    deux endroits où décider ce qui passe en comité, et ils divergeraient.
//    Décocher sort l'entrée du SUPPORT ; le drapeau et la pile restent.
// 2. **Les flèches ▲▼ sont l'alternative clavier, pas un doublon décoratif.**
//    `docs/accessibility.md` signale déjà l'absence d'alternative au
//    glisser-déposer sur la page postits : on ne reproduit pas le manque.
// 3. **Le plan compte comme le serveur rend.** L'arithmétique est partagée avec
//    `support_pptx.outline` : annoncer neuf slides et livrer onze détruirait la
//    confiance dans tout le reste de l'écran.

import {
  DEFAULT_OPTIONS,
  add,
  defaultSelection,
  candidatesFor,
  setSectionSelected,
  limitsLocked,
  move,
  outline,
  remove,
  renumber,
  type Section,
  type SupportEntry,
  type SupportOptions,
  type SupportReco,
} from '../domain/backlog-review-support.js';
import { definePage } from '../router.js';

interface Review {
  id: number;
  label: string;
  recommendations: SupportReco[];
}

let _review: Review | null = null;
let _selection: SupportEntry[] = [];
let _options: SupportOptions = { ...DEFAULT_OPTIONS };

function _reco(id: number): SupportReco | undefined {
  return _review?.recommendations.find((r) => r.id === id);
}

function _codes(reco: SupportReco): string {
  return reco.targets
    .filter((tg) => tg.type === 'action')
    .map((tg) => `#${tg.id}`)
    .join(' + ');
}

/** « Rejet proposé — Fusion #118 + #233 ». La position D'ABORD : c'est sur
 *  elle que le comité se prononce, pas sur la nature de la recommandation. */
function _entryLabel(reco: SupportReco): string {
  const family = t('review.family.' + reco.family);
  const codes = _codes(reco);
  const head = reco.pile === 'todo' ? '' : t('review.support.position.' + reco.pile) + ' — ';
  return `${head}${family}${codes ? ' ' + codes : ''}`;
}

// ── Rendu ─────────────────────────────────────────────────────────────────────

/** Une ligne de section : cochée = présentée, décochée = écartée du support.
 *
 * La case remplace la croix (2026-08-26). Retirer une entrée ne la faisait
 * disparaître que de l'écran, sans dire qu'elle existait toujours ; on ne
 * pouvait plus la faire revenir sans recharger. Une case dit les deux états et
 * se défait.
 *
 * Les flèches ▲▼ n'apparaissent que sur les lignes cochées : ordonner ce qu'on
 * ne présente pas n'a pas de sens, et elles seraient inertes.
 */
function _rowHtml(reco: SupportReco, on: boolean, first: boolean, last: boolean): string {
  const up = t('review.support.moveUp');
  const down = t('review.support.moveDown');
  const arrows = on
    ? `<button class="btn btn-secondary btn-icon" data-move="-1" data-id="${reco.id}"
        ${first ? 'disabled' : ''} aria-label="${esc(up)}" title="${esc(up)}">▲</button>
      <button class="btn btn-secondary btn-icon" data-move="1" data-id="${reco.id}"
        ${last ? 'disabled' : ''} aria-label="${esc(down)}" title="${esc(down)}">▼</button>`
    : '';
  return `<div class="list-row" ${on ? 'draggable="true"' : ''} data-entry="${reco.id}"
      style="display:flex;gap:8px;align-items:center;padding:6px 0;${on ? '' : 'opacity:.55'}">
      <input type="checkbox" data-pick-entry="${reco.id}" ${on ? 'checked' : ''}
        aria-label="${esc(t('review.support.present'))}">
      ${on ? '<span class="text-muted text-xs" style="cursor:grab">⣿</span>' : ''}
      <span style="flex:1;font-size:var(--fs-sm)">
        <strong>${esc(_entryLabel(reco))}</strong>
        <span class="text-muted"> · ${esc(reco.title)}</span>
      </span>
      ${arrows}
    </div>`;
}

function _sectionHtml(section: Section, title: string, extra: string): string {
  const chosen = _selection.filter((e) => e.section === section);
  const order = new Map(chosen.map((e, i) => [e.recommendation_id, i]));
  const all = candidatesFor(_review?.recommendations || [], section);
  // Les cochées d'abord, dans l'ordre choisi ; les décochées ensuite. Sans ce
  // regroupement, une case décochée resterait au milieu du plan et brouillerait
  // la lecture de l'ordre de passage.
  const rows = [...all].sort((a, b) => {
    const oa = order.has(a.id) ? (order.get(a.id) as number) : Number.POSITIVE_INFINITY;
    const ob = order.has(b.id) ? (order.get(b.id) as number) : Number.POSITIVE_INFINITY;
    return oa - ob || a.id - b.id;
  });
  const body = rows
    .map((reco, i) => _rowHtml(reco, order.has(reco.id), i === 0, i === chosen.length - 1))
    .join('');
  const toggles = all.length
    ? `<button class="btn btn-secondary btn-sm" data-all="${section}">${esc(t('review.support.selectAll'))}</button>
       <button class="btn btn-secondary btn-sm" data-none="${section}">${esc(t('review.support.selectNone'))}</button>`
    : '';
  return `<div class="card" data-drop-section="${section}" style="padding:12px;margin-bottom:12px">
      <div class="card-title" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
        <span style="flex:1">${esc(title)} <span class="text-muted">${chosen.length}/${all.length}</span></span>
        ${toggles}${extra}
      </div>
      ${body || `<div class="empty" style="padding:8px">${esc(t('review.support.sectionEmpty'))}</div>`}
    </div>`;
}

function _optionsHtml(locked: boolean): string {
  const box = (key: keyof SupportOptions, label: string, disabled = false): string =>
    `<label style="display:block;margin:4px 0">
        <input type="checkbox" data-option="${key}" ${_options[key] ? 'checked' : ''}
          ${disabled ? 'disabled' : ''}> ${esc(label)}
      </label>`;
  return `<div class="card" style="padding:12px">
      <div class="card-title">${esc(t('review.support.options'))}</div>
      ${box('annexe', t('review.support.optAnnexe'))}
      ${box('limites', t('review.support.optLimites'), locked)}
      ${locked ? `<div class="text-muted text-xs">${esc(t('review.support.limitesLocked'))}</div>` : ''}
      ${box('annotations', t('review.support.optAnnotations'))}
    </div>`;
}

function _planHtml(): string {
  const plan = outline(_selection, _review?.recommendations || [], _options);
  const families = plan.families
    .map((f) => {
      const n = _selection.filter(
        (e) => e.section === 'arbitrate' && _reco(e.recommendation_id)?.family === f,
      ).length;
      return `<li>${esc(t('review.family.' + f))} (${n})</li>`;
    })
    .join('');
  return `<div class="card" style="padding:12px">
      <div class="card-title">${esc(t('review.support.plan'))}</div>
      <ol style="font-size:var(--fs-sm);padding-left:18px;margin:8px 0">
        <li>${esc(t('review.support.planContext'))}</li>
        <li>${esc(t('review.support.planConstat'))}</li>
        <li>${esc(t('review.support.planMain'))}</li>
        <li>${esc(t('review.support.planDecisions'))}
          <ul style="padding-left:16px">${families}</ul>
        </li>
        <li>${esc(t('review.support.planInfo'))} (${plan.info})</li>
        ${_options.annexe ? `<li>${esc(t('review.support.planAnnex'))}</li>` : ''}
      </ol>
      <div style="font-weight:600">→ ${plan.slides} ${esc(t('review.support.slides'))}</div>
      ${plan.grouped ? `<div class="text-muted text-xs">${esc(t('review.support.groupedNotice'))}</div>` : ''}
    </div>`;
}

function _render(): void {
  if (!_review) return;
  const locked = limitsLocked(_review.recommendations);
  if (locked) _options.limites = true;
  setContent(`<div style="display:grid;grid-template-columns:1fr 320px;gap:16px;align-items:start">
      <div>
        ${_sectionHtml('arbitrate', '⚑ ' + t('review.support.arbitrate'), '')}
        ${_sectionHtml('info', t('review.support.info'), '')}
        ${_optionsHtml(locked)}
      </div>
      ${_planHtml()}
    </div>`);
  _bind();
}

// ── Interactions ──────────────────────────────────────────────────────────────

function _apply(next: SupportEntry[]): void {
  _selection = renumber(next);
  _render();
}

function _bind(): void {
  document.querySelectorAll<HTMLElement>('[data-move]').forEach((btn) => {
    btn.addEventListener('click', () =>
      _apply(move(_selection, Number(btn.dataset.id), Number(btn.dataset.move))),
    );
  });
  document.querySelectorAll<HTMLInputElement>('[data-pick-entry]').forEach((box) => {
    box.addEventListener('change', () => {
      const id = Number(box.dataset.pickEntry);
      const reco = _reco(id);
      if (!reco) return;
      const section: Section = reco.to_arbitrate ? 'arbitrate' : 'info';
      _apply(box.checked ? add(_selection, id, section) : remove(_selection, id));
    });
  });
  document.querySelectorAll<HTMLElement>('[data-all]').forEach((btn) => {
    btn.addEventListener('click', () =>
      _apply(
        setSectionSelected(
          _selection,
          _review?.recommendations || [],
          btn.dataset.all as Section,
          true,
        ),
      ),
    );
  });
  document.querySelectorAll<HTMLElement>('[data-none]').forEach((btn) => {
    btn.addEventListener('click', () =>
      _apply(
        setSectionSelected(
          _selection,
          _review?.recommendations || [],
          btn.dataset.none as Section,
          false,
        ),
      ),
    );
  });
  document.querySelectorAll<HTMLInputElement>('[data-option]').forEach((box) => {
    box.addEventListener('change', () => {
      _options = { ..._options, [box.dataset.option as keyof SupportOptions]: box.checked };
      _render();
    });
  });
  _bindDrag();
  const btn = document.getElementById('btn-export-support') as HTMLButtonElement | null;
  if (btn) btn.disabled = _selection.length === 0;
}

function _bindDrag(): void {
  let dragged: number | null = null;
  document.querySelectorAll<HTMLElement>('[data-entry]').forEach((row) => {
    row.addEventListener('dragstart', () => {
      dragged = Number(row.dataset.entry);
    });
    row.addEventListener('dragover', (e) => e.preventDefault());
    row.addEventListener('drop', (e) => {
      e.preventDefault();
      const over = Number(row.dataset.entry);
      const from = dragged;
      dragged = null;
      if (!from || from === over) return;
      // Le déposé prend la place du survolé, dans SA section. Un déplacement
      // entre sections n'existe pas : « à trancher » suit le drapeau.
      const ordered = renumber(_selection);
      const a = ordered.find((x) => x.recommendation_id === from);
      const b = ordered.find((x) => x.recommendation_id === over);
      if (!a || !b || a.section !== b.section) return;
      _apply(move(ordered, from, b.position - a.position > 0 ? 1 : -1));
    });
  });
}

// La modale « Ajouter » a été retirée le 2026-08-26 : les deux sections se
// remplissent seules et se vident à la case. Ajouter à la main aurait fait deux
// endroits où décider ce qui passe en comité, et ils auraient divergé.

async function _export(): Promise<void> {
  if (!_review) return;
  // L'identifiant est capturé AVANT la fermeture : elle s'exécute plus tard, et
  // `_review` peut avoir changé entre-temps si l'utilisateur a navigué. On
  // produit le support de la revue qu'il avait sous les yeux, pas d'une autre.
  const reviewId = _review.id;
  const btn = document.getElementById('btn-export-support') as HTMLButtonElement | null;
  await api.withButton(btn, async () => {
    const body = (await api.createBacklogSupport(reviewId, {
      selection: _selection,
      options: _options,
    })) as { support: { id: number; filename: string }; removed: Array<{ title?: string }> };
    if (body.removed.length) {
      // Retirées, pas signalées : une slide de comité fondée sur une action
      // disparue est un mensonge, pas une nuance.
      toast(t('review.support.removed', { n: body.removed.length }), 'error');
    }
    const out = await api.exportBacklogSupport(reviewId, body.support.id, body.support.filename);
    toast(t('review.support.exported', { name: out.filename }));
  });
}

pages.backlogReviewSupport = definePage<'review/:id/support'>(async ({ id }) => {
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    _review = (await api.getBacklogReview(id)) as Review;
  } catch (e: unknown) {
    setContent(
      `<div class="error">${esc(e instanceof Error ? e.message : t('common.error'))}</div>`,
    );
    return;
  }
  _selection = defaultSelection(_review.recommendations);
  _options = { ...DEFAULT_OPTIONS };
  setHeader(
    t('review.support.title', { scope: _review.label }),
    `<a class="btn btn-secondary" href="#review/${_review.id}">${esc(t('common.back'))}</a>
     <button class="btn btn-primary" id="btn-export-support">${esc(t('review.support.export'))}</button>`,
  );
  document.getElementById('btn-export-support')?.addEventListener('click', () => void _export());
  _render();
});
